export class ApiError extends Error { constructor(public code: string, message: string, public status = 0) { super(message); } }

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: opts.method || (opts.body ? "POST" : "GET"), headers: { "content-type": "application/json" }, credentials: "same-origin", body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined });
  } catch { throw new ApiError("offline", "You seem to be offline. Check the internet and try again."); }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data.error || "server_error", data.message || "Something went wrong.", res.status);
  return data as T;
}

export type TutorMode = "chat" | "stuck" | "rephrase" | "explain" | "photo" | "story" | "report";
export interface TutorBody {
  mode: TutorMode; messages?: { role: "user" | "assistant"; content: string }[];
  question?: { text: string; answer: string | number; tid?: string } | null; topicId?: string;
  stuckKind?: string; image?: string; seed?: string; nums?: number[];
}

/** Ask Numi (Claude on our server). Streams text as it arrives; resolves with the full reply. */
export async function tutor(childId: string, body: TutorBody, onText?: (full: string) => void, signal?: AbortSignal): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`/api/tutor/${childId}`, { method: "POST", headers: { "content-type": "application/json" }, credentials: "same-origin", body: JSON.stringify(body), signal });
  } catch (e: any) { throw new ApiError(e?.name === "AbortError" ? "cancelled" : "offline", "You seem to be offline."); }
  if (!res.ok || !res.body) { const j = await res.json().catch(() => ({})); throw new ApiError(j.error || "upstream_error", j.message || "Numi couldn't answer just now.", res.status); }
  const reader = res.body.getReader(); const dec = new TextDecoder();
  let buf = "", full = "", done = "";
  for (;;) {
    const { value, done: end } = await reader.read().catch((e) => { throw new ApiError(e?.name === "AbortError" ? "cancelled" : "upstream_error", full); });
    if (end) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, i); buf = buf.slice(i + 2);
      const ev = /^event: (.*)$/m.exec(chunk)?.[1]; const data = /^data: (.*)$/m.exec(chunk)?.[1];
      if (!ev || !data) continue;
      const j = JSON.parse(data);
      if (ev === "text") { full += j.delta; onText?.(full); }
      else if (ev === "done") done = j.text;
      else if (ev === "error") throw new ApiError(j.code, j.message);
    }
  }
  return done || full;
}

/** Shrink a photo before upload so it's fast on mobile data. */
export async function photoToDataUrl(file: File, max = 1600): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, bad) => { const i = new Image(); i.onload = () => ok(i); i.onerror = bad; i.src = url; });
    const k = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas"); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.85);
  } finally { URL.revokeObjectURL(url); }
}
